import { useState } from "react"
import { Action, Badge, ButtonKind, Column, FieldType, FieldValue, ModalId, SpinnerDiv, UIPermission, useUIActions } from "../../../../../features/ui"
import { Data, Data2, DataTypeList, Labels } from "../../../../../features/data-types"
import { useSystemStateHooks } from "../../../../system"
import { DataType, TableName } from "../../../../../tables"
import { PaymentOrderFilters } from "./paymentOrderFilters"
import { define } from "../../../../../utils/typeUtils"
import { PaymentOrder, PaymentOrderStatus, Ticket } from "../../../state/types"
import { useLocation } from "react-router-dom"
import { ConfirmPaymentFormModal } from "../confirm-payment-modal/confirmPaymentFormModal"
import { DownloadPaymentOrderFilesModal } from "../download-payment-order-files-modal/downloadPaymentOrderFilesModal"
import { NO_SUPPLIER_LABEL, Supplier } from "../../../../purchases"

type Props = {
    paymentOrders: PaymentOrder[]
    suppliers: Supplier[]
    ticketsMap: Map<string, Ticket[]>
    supplierMap: Map<string, string>
}

export const PaymentOrderList = (props: Props) => {
    const { paymentOrders, suppliers, ticketsMap, supplierMap } = props

    const [rows, setRows] = useState<Data2[]>([])
    const [currentPaymentOrder, setCurrentPaymentOrder] = useState<PaymentOrder>()
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PAYMENT_ORDERS)
    const { pathname } = useLocation()

    const { openModal } = useUIActions()

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'payedAmount',
            type: FieldType.PRICE,
            label: 'Importe'
        },
        {
            name: 'creationDate',
            type: FieldType.DATE,
            label: 'Fecha de Emisión'
        },
        {
            name: 'dueDate',
            type: FieldType.DATE,
            label: 'Fecha de Vto.'
        },
        {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            label: 'Proveedor',
            render: (value?: FieldValue) => {
                const supplier = suppliers.find(supplier => supplier.id === value)
                return <Badge label={supplier?.name || NO_SUPPLIER_LABEL} />
            },
            sortValue: (value?: FieldValue) => suppliers.find(s => s.id === value)?.name ?? NO_SUPPLIER_LABEL
        },
        {
            name: 'status',
            type: FieldType.TEXT,
            label: 'Estado',
            sortValue: (value?: FieldValue) => Labels.paymentOrderStatusLabel(value as PaymentOrderStatus),
            render: (value?: FieldValue) => Labels.paymentOrderStatusLabel(define(value) as PaymentOrderStatus)
        }
    ]

    const findPaymentOrder = (id?: string) => define(paymentOrders.find(paymentOrder => paymentOrder.id === id))

    const onDownloadFiles = (id?: string) => {
        setCurrentPaymentOrder(findPaymentOrder(id))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onConfirmPayment = (id?: string) => {
        setCurrentPaymentOrder(findPaymentOrder(id))
        openModal(ModalId.CONFIRM_PAYMENT_FORM)
    }

    const actions = (id?: string): Action[] => {
        const paymentOrder = findPaymentOrder(id)
        const isPaymentEmitted = paymentOrder.status === PaymentOrderStatus.EMITTED

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary' as ButtonKind, tooltip: 'Ver Detalle', href: `${pathname}/${id!}` },
            { icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles }
        ]
        if (!readOnly && isPaymentEmitted) {
            actionList.push({ icon: 'check-lg', tooltip: 'Confirmar Pago', onClick: onConfirmPayment })
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        const paymentOrders = dataTypes as PaymentOrder[]
        const newRows: Data2[] = paymentOrders.map(paymentOrder => {
            const tickets = ticketsMap.get(paymentOrder.id) || []
            const dueDate = new Date(Math.min(...tickets.map(ticket => new Date(ticket.dueDate).getTime())))
            const supplierId = supplierMap.get(paymentOrder.id)
            return {
                ...paymentOrder,
                dueDate,
                supplierId
            }    
        })
        setRows(newRows)
        setAsyncDataLoaded(true)
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <PaymentOrderFilters
                paymentOrders={paymentOrders}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows as Data[]}
                    actions={actions}
                    readOnly
                />
            )}
            <DownloadPaymentOrderFilesModal paymentOrder={currentPaymentOrder} />
            <ConfirmPaymentFormModal paymentOrder={currentPaymentOrder} />
        </>
    )
}
